// Account & Settings hooks — the WRAPPER layer (lint-exempt for raw TanStack Query).
// Account + features + change-password are PERSONAL surfaces, so they live under the ['me', …] namespace
// (immune to the band-scoped realtime invalidation registry, which only touches ['band', signal.id, …]); the
// account/features reads carry the active bandId in their key because the endpoint is band-scoped (it returns
// the caller's role + artist name ON THIS BAND). Change-password persists the fresh token through the
// SAME TokenProvider mechanism login uses — no token ever touches localStorage.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { isNative } from "@cntrl/native";
import { useApp } from "../appContext.js";
import { useBandContext } from "../bandContext.js";
import { ApiError } from "./apiError.js";

type AccountReadResponse = components["schemas"]["AccountReadResponse"];
type FeaturesResponse = components["schemas"]["FeaturesResponse"];
type ChangePasswordRequest = components["schemas"]["ChangePasswordRequest"];
type AuthTokenResponse = components["schemas"]["AuthTokenResponse"];

export type { AccountReadResponse, FeaturesResponse };

/** `GET /bands/{bandId}/settings/account` — read-only artist / role / email / member-since (Account tab).*/
export function useAccount() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  return useQuery({
    queryKey: ["me", "account", bandId],
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/settings/account", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    staleTime: 60_000,
  });
}

/** `GET /bands/{bandId}/settings/features` — guides / roadmap / coming-soon (Features tab; ships EMPTY).*/
export function useFeatures() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  return useQuery({
    queryKey: ["me", "features", bandId],
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/settings/features", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    staleTime: 60_000,
  });
}

/**
 * `POST /auth/change-password` ({current, newPassword}) → the login shape (a FRESH session token). The response
 * token REPLACES the caller's session through the TokenProvider (the same setBearer login uses); skip that and
 * the caller 401s themselves out on the next request. The mutation throws on a non-2xx / tokenless body so the
 * wrong-current-password 4xx surfaces verbatim to the form — never a phantom success. The
 * `/auth/*` route is exempt from the global clear-on-401, so a bad attempt does not log the user out.
 */
export function useChangePassword() {
  const { api, tokenProvider } = useApp();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { current: string; newPassword: string }): Promise<AuthTokenResponse> => {
      const body: ChangePasswordRequest = {
        current: input.current,
        newPassword: input.newPassword,
        client: isNative() ? "ios" : "web",
      };
      const { data, error, response } = await api.POST("/api/v1/auth/change-password", { body });
      if (!response.ok || !data?.token) throw new ApiError(response.status, error);
      await tokenProvider.setBearer(data.token);
      return data;
    },
    onSuccess: () => {
      // The principal is unchanged but the session rotated — refresh /me so any token-derived view agrees.
      void qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}
